import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import { isPortfolioTreeStrategy, isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import type { IPortfolioTree } from '@/types/IPortfolioTree';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import { createGlobalState } from '@vueuse/shared';
import { ref, computed } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import { ResourceLoadingFailReason } from '@/constants/ResourceLoadingFailReason';
import { parseResponseError, KnownError } from '@/utils/parseResponseError';
import { getRootItemUnderAnalysis, isOfficialMasterIndexFn, isTaggedAsLiveEquityBasketFn } from '@/utils/portfolioTree';
import { findStrategyBenchmarkInternal, findValidBenchmark } from '@/utils/benchmark';
import { isPortfolioItemResponseDTO, isStrategyItemResponseDTO } from '@/api-v2/web/discover';
import type { DSBPortfolioItemResponseDTO, DSBStrategyItemResponseDTO } from '@/api-v2/web/discover';
import type { AnalyticsTrackTypeConstants } from '@/types/AnalyticsTrackTypeConstants';
import useTranslation from './useTranslation';
import { useGetDiscoverIndexAndType } from '@/composables/useGetDiscoverUtils';
import { useDiscoverStore } from './useDiscoverStore';
import type { IndexOptionType } from '@/api-v2/web/discover/types/IndexOptionType';

const DEBUG = false;

export const useItemUnderAnalysisGlobalState = createGlobalState(
  (
    debug?: boolean,
  ): {
    itemUnderAnalysis: Ref<IItemUnderAnalysis | null>;
    rootItemUnderAnalysis: Ref<IItemUnderAnalysis | null>;
    isLoadingOrFetching: Ref<boolean | null>;

    /**
     * Please note that this will exist *WHENEVER* we get a response back from the `/draft` endpoint,
     * whether it's a classic 'portfolio', master index, or an equity basket/QIS of stocks.
     */
    portfolioTreeUnderAnalysis: Ref<IPortfolioTree | null>;

    /**
     * If this variable is true:
     * - Portfolio Construction will always be visible
     * - we will always route to the Strategy Factsheet
     */
    shouldExposePositions: Ref<boolean | null>;
    containsOnlyEquities: Ref<boolean | null>;

    hasSnapshots: Ref<boolean | null>;
    hasBacktests: Ref<boolean | null>;

    /**
     * If we get a response back from the `/draft` endpoint, we should treat the items as portfolios under the following
     * circumstances:
     * - if `shouldExposePositions` or `containsOnlyEquities` is `undefined` (classic portfolio case), we treat the item
     *    as a portfolio on all routes.
     * - if `shouldExposePositions` is `true` and `containsOnlyEquities` is `false` (master index case), we treat the item
     *    as a portfolio on all routes except for Factsheet, in which case we route to the Strategy Factsheet.
     * - if `shouldExposePositions` is `true and `containsOnlyEquities` is `true` (live equity basket case), we ONLY
     *    treat the item as a portfolio on Portfolio Construction, and treat it as a Strategy everywhere else.
     *
     * If this variable is true, then all portfolio tree APIs should be called with the `draftSlug` as the `indexUniqueIdentifier`.
     * If it is false, then all strategy APIs should be called with the `code` as the `indexUniqueIdentifier`.
     *
     * This variable will return `null` if `itemUnderAnalysis` is `null`.
     *
     * Please note that this variable should *ONLY* be used for enabling/disabling data, etc.
     *
     * Because the behavior of the itemUnderAnalysis differs for master indices and live equity baskets based on a given
     * route, this variable should NOT be used when determining which version of a page (portfolio or strategy) we should
     * route to. For that functionality, please use `getShouldUseDraftOfRootItemOnRouteFn`.
     */
    shouldTreatRootItemAsPortfolio: Ref<boolean | null>;

    /**
     * For the constituent tools (Position and Risk), we need to ensure that master indices are always treated as portfolios,
     * and live equity baskets are always treated as strategies regardless of which route we are on.
     *
     * We need this variable *in addition to* `shouldTreatRootItemAsPortfolio` because the Navigation Panel still requires us
     * to have `shouldTreatRootItemAsPortfolio = true` while we are on the Portfolio Composition subpage,
     * but the charts and tables in the Position tools need to use the strategy data.
     *
     * This is due to an API limitation where our Position and Risk APIs do not support a portfolio of instruments (non-QIS),
     * and we will not be refactoring this as we are expecting to switch to use Algo Quant for Equity Baskets
     * for the whole portfolio construction by the end of Q1 2026
     */
    shouldTreatRootItemAsPortfolioOnConstituentTools: Ref<boolean | null>;

    /**
     * For equity baskets and master indices, we could use the draft or the strategy itself. This has implications on
     * the API calls we make - if we use the draft, we will be using our re-constructed version of the strategy.
     * If there's any mistake in this process, the results of this API will not match that of the strategy.
     *
     * As such, we use this variable to determine whether to use the draft or the strategy itself.
     *
     * For equity baskets, we use the strategy *everywhere*, except for Portfolio Construction.
     * With the exception of the graphs and table on the subpage "Composition" under Portfolio Construction which will still use the strategy.
     * and will be using the variable `shouldTreatRootItemAsPortfolioOnConstituentTools` to determine this for Position and Risk tools.
     *
     * For master indices, we use the draft *everywhere*, except for the Factsheet (we use the Strategy Factsheet, at least for now)
     * and Performance Contribution.
     */
    shouldUseDraftOfRootItem: Ref<boolean | null>;

    hasPerformanceContributionData: Ref<boolean | null>;
    hasPositionData: Ref<boolean | null>;
    hasRiskData: Ref<boolean | null>;
    hasBarraData: Ref<boolean | null>;

    defaultModeDraft: Ref<IPortfolioTree | null>;
    draft: Ref<IPortfolioTree | null>;
    originalSlug: Ref<string | null>;
    draftSlug: Ref<string | null>;
    defaultModeDraftError: Ref<Error | null>;
    disabledBeforeDate: ComputedRef<string | null>;
    liveDate: ComputedRef<string | null>;

    /**
     * This variable serves as the source of truth for when we are editing the portfolio tree in some respect,
     * adding or removing portfolios, changing the weighting, etc.
     *
     * The flow of logic looks like this:
     * 1. We fetch a draft using the `usePortfolioTreeDraft` composable.
     * 2. Changes are propagated to the `masterPortfolioTreeTempRef` variable returned by this function
     *      via the `useSetMasterPortfolioTreeOnDraftChange` composable.
     * 3. After we have made our changes to the tree, we call the `useCalculateDraft` mutation, which updates the
     *      vue-query draft cache in `onSuccess`.
     * 4. Changes are propagated to the `masterPortfolioTreeTempRef` variable returned by this function
     *      via the `useSetMasterPortfolioTreeOnDraftChange` composable.
     *
     * Please note that this is the only variable that should be supplied to the `useCalculateDraft` mutation.
     * As a result, this variable only contains a portfolio tree that has no view mode applied.
     */
    masterPortfolioTreeTempRef: Ref<IPortfolioTree | null>;

    portfolioTreeLoadingFailed: Ref<boolean>;
    portfolioTreeFailureReason: Ref<ResourceLoadingFailReason | null>;

    /**
     * We only want to enable the redirect if the itemUnderAnalysis is loaded and the indexUniqueIdentifier is the same
     * as the itemUnderAnalysis. Otherwise, we get some weird redirect behavior while we are processing the itemUnderAnalysis.
     */
    getCanRedirect: ({
      indexUniqueIdentifier,
    }: {
      indexUniqueIdentifier: Ref<string | undefined>;
    }) => ComputedRef<boolean | null>;

    /**
     * The savedBenchmarkCode refers to the root item under analysis's benchmark.
     *
     * If the user has saved a benchmark, this will be the user's benchmark.
     *
     * If the user has not saved a benchmark, this will be the organization's default benchmark if it exists.
     *
     * `null` means that there is no saved benchmark by the user or the organization.
     *
     * `undefined` means that we have not yet loaded whether there is a saved benchmark by the user or the organization.
     *
     * FIXME: WAA-14309 - product has not yet determined if we should propagate the benchmark from the factsheet entity
     * to the draft entity when `shouldTreatRootItemAsPortfolio` is true.
     */
    savedBenchmarkCode: ComputedRef<string | null | undefined>;

    /**
     * `null` means that there is no saved benchmark by the user or the organization.
     *
     * `undefined` means that we have a `savedBenchmarkCode`, but there was a problem fetching the item corresponding to this code.
     */
    savedBenchmark: ComputedRef<DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined>;
    savedBenchmarkType: ComputedRef<AnalyticsTrackTypeConstants | null>;
    savedBenchmarkName: ComputedRef<string | null>;

    /**
     * The `draftBenchmarkCode` is designed to be used temporarily by the user to test out updating the benchmark to
     * another value. It will not be persisted to the database if the user navigates away or closes the window.
     *
     * `null` means that the user has no draft benchmark
     *
     * Whereas `savedBenchmarkCode` may be `undefined`, this variable may not. It either exists or it does not.
     */
    draftBenchmarkCode: Ref<string | null>;

    /**
     * `null` means that there is no draft benchmark
     *
     * `undefined` means that we have a `draftBenchmarkCode`, but there was a problem fetching the item corresponding to this code.
     */
    draftBenchmark: Ref<DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined>;
    draftBenchmarkType: Ref<AnalyticsTrackTypeConstants | null>;

    /**
     * The organization's default benchmark for the strategy. This must always be shown on the Strategy Static Info Table,
     * but it may be overridden by the user's benchmark, `userStrategyBenchmarkOverrideCode`.
     *
     * `undefined` will be returned when the
     */
    internalBenchmarkCode: Ref<string | null | undefined>;

    /**
     * `null` means that there is no user strategy benchmark override
     *
     * `undefined` means that the root item under analysis is not a strategy or is not yet loaded
     */
    userStrategyBenchmarkOverrideCode: Ref<string | null | undefined>;

    /**
     * The savedReferenceCode refers to the root item under analysis's reference
     *
     * `null` means that there is no saved reference for the given portfolio
     *
     * `undefined` means that the draft has not yet loaded
     */
    savedReferenceCode: ComputedRef<string | null | undefined>;

    /**
     * `null` means that there is no saved reference for the given portfolio
     *
     * `undefined` means that we have a `savedReferenceCode`, but there was a problem fetching the item corresponding to this code.
     */
    savedReference: ComputedRef<DSBPortfolioItemResponseDTO | DSBStrategyItemResponseDTO | null | undefined>;
    savedReferenceType: ComputedRef<AnalyticsTrackTypeConstants | null>;
  } => {
    const shouldExposePositions = ref<boolean | null>(null);

    const containsOnlyEquities = ref<boolean | null>(null);

    const itemUnderAnalysis = ref<IItemUnderAnalysis | null>(null);
    const shouldUseDraftOfRootItem = ref<boolean | null>(null);

    const portfolioTreeUnderAnalysis = ref<IPortfolioTree | null>(null);

    const draft = ref<IPortfolioTree | null>(null);

    const defaultModeDraftError = ref<Error | null>(null);

    const disabledBeforeDate = computed((): string | null => {
      return getDisabledBeforeDate(itemUnderAnalysis.value);
    });

    const liveDate = computed((): string | null => {
      return getLiveDate(itemUnderAnalysis.value);
    });

    const shouldTreatRootItemAsPortfolio = computed((): boolean | null => {
      if (itemUnderAnalysis.value === null || shouldUseDraftOfRootItem.value == null) {
        return null;
      }

      return shouldUseDraftOfRootItem.value;
    });

    const rootItemUnderAnalysis = computed(() =>
      getRootItemUnderAnalysis(itemUnderAnalysis.value, portfolioTreeUnderAnalysis.value),
    );

    const shouldTreatRootItemAsPortfolioOnConstituentTools = computed(() => {
      const datumToUse = getRootItemUnderAnalysis(itemUnderAnalysis.value, portfolioTreeUnderAnalysis.value);
      if (!datumToUse) return null;

      if (isTaggedAsLiveEquityBasketFn(datumToUse)) {
        return false;
      }

      if (isOfficialMasterIndexFn(datumToUse)) {
        return true;
      }

      return isPortfolioTreeSubportfolio(datumToUse)
        ? // classic portfolio case
          !datumToUse.apiConfig?.shouldExposePositions || !datumToUse.apiConfig?.containsOnlyEquities
        : // otherwise return false
          false;
    });

    const isLoadingOrFetching = ref<boolean | null>(null);

    const getCanRedirect = ({
      indexUniqueIdentifier,
    }: {
      indexUniqueIdentifier: Ref<string | undefined>;
    }): ComputedRef<boolean | null> => {
      return computed((): boolean | null => {
        if (shouldTreatRootItemAsPortfolio.value == null) {
          return null;
        }

        if (isLoadingOrFetching.value) {
          return false;
        }

        if (isStrategyFactsheetResponseDTO(itemUnderAnalysis.value)) {
          return indexUniqueIdentifier.value === itemUnderAnalysis.value.strategy.code;
        }

        // draft portfolio must match current item under analysis
        if (
          isPortfolioTreeSubportfolio(rootItemUnderAnalysis.value) &&
          draft.value?.portfolioId !== rootItemUnderAnalysis.value.portfolioTreeId
        )
          return false;

        return (
          // only allow redirect if the originalSlug is the same as the indexUniqueIdentifier
          portfolioTreeUnderAnalysis.value?.originalSlug === indexUniqueIdentifier.value ||
          // or if the reference is the same as the indexUniqueIdentifier (for master indices / baskets)
          portfolioTreeUnderAnalysis.value?.portfolioTree.reference === indexUniqueIdentifier.value
        );
      });
    };

    const portfolioTreeLoadingFailed = computed(() => {
      return !draft.value && defaultModeDraftError.value !== null;
    });

    const portfolioTreeFailureReason = computed((): ResourceLoadingFailReason | null => {
      if (draft.value && !portfolioTreeLoadingFailed.value) return null;

      const error = defaultModeDraftError.value;
      if (!error) return null;

      const parsedError = parseResponseError(error);
      return parsedError === KnownError.NetworkError
        ? ResourceLoadingFailReason.NetworkError
        : ResourceLoadingFailReason.Other;
    });

    /**
     * Returns `undefined` when the draft is not loaded
     * Returns `null` when no benchmark is found
     */
    const currentPortfolioTreeBenchmarkCode = computed((): string | null | undefined => {
      if (DEBUG || debug) console.log('currentPortfolioTreeBenchmarkCode re-running');

      if (!draft.value) {
        if (DEBUG || debug)
          console.log('currentPortfolioTreeBenchmarkCode returning undefined because draft is not loaded');

        return undefined;
      }

      return findValidBenchmark(draft.value, draft.value?.portfolioTree, DEBUG || debug);
    });

    const { allPortfoliosById, allTracksById } = useDiscoverStore();

    const userStrategyBenchmarkOverrideCode = computed((): string | null | undefined => {
      if (!rootItemUnderAnalysis.value || !isStrategyFactsheetResponseDTO(rootItemUnderAnalysis.value)) {
        return undefined;
      }

      const userBenchmark = rootItemUnderAnalysis.value?.associatedAttributes?.benchmark;
      if (!userBenchmark) {
        if (DEBUG || debug) console.log('getUserStrategyBenchmarkOverride returning null');

        return null;
      }

      if ('portfolioId' in userBenchmark) {
        const retval = allPortfoliosById.value.get(userBenchmark.portfolioId)?.slug;
        if (DEBUG || debug) console.log('getUserStrategyBenchmarkOverride returning', retval);

        return retval;
      }

      const retval = allTracksById.value.get(userBenchmark.timeSeriesId)?.code;
      if (DEBUG || debug) console.log('getUserStrategyBenchmarkOverride returning', retval);

      return retval;
    });

    const internalBenchmarkCode = computed((): string | null | undefined => {
      if (!rootItemUnderAnalysis.value) return undefined;

      return findStrategyBenchmarkInternal({ factsheet: rootItemUnderAnalysis.value, debug: DEBUG || debug }) ?? null;
    });

    /**
     * If the user has defined a benchmark, we return that benchmark code.
     * Otherwise, we return the organization's default benchmark code if it exists.
     */
    const currentStrategyBenchmarkCode = computed(() => {
      if (DEBUG || debug) console.log('currentStrategyBenchmarkCode re-running');

      if (
        !rootItemUnderAnalysis.value ||
        shouldTreatRootItemAsPortfolio.value === true ||
        shouldTreatRootItemAsPortfolio.value === null
      ) {
        if (DEBUG || debug)
          console.log(
            'currentStrategyBenchmarkCode returning undefined because rootItemUnderAnalysis is not loaded or shouldTreatRootItemAsPortfolio is true',
          );

        return undefined;
      }

      if (DEBUG || debug)
        console.log(
          'currentStrategyBenchmarkCode returning userStrategyBenchmarkOverrideCode or internalBenchmarkCode',
          {
            rootItemUnderAnalysis: rootItemUnderAnalysis.value,
            shouldTreatRootItemAsPortfolio: shouldTreatRootItemAsPortfolio.value,
            userStrategyBenchmarkOverrideCode: userStrategyBenchmarkOverrideCode.value,
            internalBenchmarkCode: internalBenchmarkCode.value,
          },
        );

      return userStrategyBenchmarkOverrideCode.value ?? internalBenchmarkCode.value ?? null;
    });

    const savedBenchmarkCode = computed((): string | null | undefined => {
      if (DEBUG || debug) console.log('savedBenchmarkCode re-running');

      if (!rootItemUnderAnalysis.value) {
        if (DEBUG || debug)
          console.log('savedBenchmarkCode returning undefined because rootItemUnderAnalysis is not loaded');

        return undefined;
      }

      /**
       * TODO: WAA-14309 - product has not yet determined if we should propagate the benchmark from the factsheet entity
       * to the draft entity when `shouldTreatRootItemAsPortfolio` is true.
       */
      if (shouldTreatRootItemAsPortfolio.value) {
        if (DEBUG || debug)
          console.log(
            'savedBenchmarkCode returning currentPortfolioTreeBenchmarkCode',
            currentPortfolioTreeBenchmarkCode.value,
          );

        return currentPortfolioTreeBenchmarkCode.value;
      }

      if (DEBUG || debug)
        console.log('savedBenchmarkCode returning currentStrategyBenchmarkCode', currentStrategyBenchmarkCode.value);

      return currentStrategyBenchmarkCode.value;
    });

    const { index: savedBenchmark, type: savedBenchmarkType } = useGetDiscoverIndexAndType(savedBenchmarkCode);

    /**
     * Returns `undefined` when the draft is not loaded
     * Returns `null` when no reference is found
     */
    const savedReferenceCode = computed((): string | null | undefined => {
      if (DEBUG || debug) console.log('savedReferenceCode re-running');

      if (isStrategyFactsheetResponseDTO(rootItemUnderAnalysis.value)) return null;

      if (!draft.value) {
        if (DEBUG || debug) console.log('savedReferenceCode returning undefined because draft is not loaded');

        return undefined;
      }

      // Both reference and userReference should be able to see the reconciliation page
      return rootItemUnderAnalysis.value?.reference ?? rootItemUnderAnalysis.value?.userReference ?? null;
    });
    const { index: savedReference, type: savedReferenceType } = useGetDiscoverIndexAndType(savedReferenceCode);

    const { translate } = useTranslation();

    const savedBenchmarkName = computed(() => {
      if (savedBenchmark.value === undefined) {
        return translate({
          path: 'FACTSHEETS.PERFORMANCE_SUMMARY.BENCHMARK_UNAVAILABLE',
        });
      }

      if (savedBenchmark.value === null) {
        return '';
      }

      if ('code' in savedBenchmark.value) return savedBenchmark.value.displayName;

      return savedBenchmark.value.displayName;
    });

    const draftBenchmarkCode = ref<string | null>(null);
    const { index: draftBenchmark, type: draftBenchmarkType } = useGetDiscoverIndexAndType(draftBenchmarkCode);

    return {
      draft,
      itemUnderAnalysis,
      rootItemUnderAnalysis,
      isLoadingOrFetching,
      shouldExposePositions,
      containsOnlyEquities,
      shouldTreatRootItemAsPortfolio,
      shouldTreatRootItemAsPortfolioOnConstituentTools,
      shouldUseDraftOfRootItem,
      portfolioTreeUnderAnalysis,
      hasSnapshots: ref<boolean | null>(null),
      hasBacktests: ref<boolean | null>(null),
      hasPerformanceContributionData: ref<boolean | null>(null),
      hasPositionData: ref<boolean | null>(null),
      hasRiskData: ref<boolean | null>(null),
      hasBarraData: ref<boolean | null>(null),
      defaultModeDraft: ref<IPortfolioTree | null>(null),
      originalSlug: ref<string | null>(null),
      draftSlug: ref<string | null>(null),
      defaultModeDraftError,
      disabledBeforeDate,
      liveDate,
      portfolioTreeLoadingFailed,
      portfolioTreeFailureReason,
      masterPortfolioTreeTempRef: ref<IPortfolioTree | null>(null),
      getCanRedirect,

      savedBenchmark,
      savedBenchmarkType,
      savedBenchmarkName,
      internalBenchmarkCode,
      savedBenchmarkCode,
      userStrategyBenchmarkOverrideCode,

      draftBenchmarkCode,
      draftBenchmark,
      draftBenchmarkType,

      savedReferenceCode,
      savedReference,
      savedReferenceType,
    };
  },
);

const getDisabledBeforeDateToUse = ({
  historyStartDate,
  effectiveHistoryStartDate,
}: {
  historyStartDate: string | null | undefined;
  effectiveHistoryStartDate: string | null | undefined;
}): string | null => {
  if (!historyStartDate) {
    return null;
  }

  if (effectiveHistoryStartDate && effectiveHistoryStartDate > historyStartDate) {
    return effectiveHistoryStartDate;
  }

  return historyStartDate;
};

export const getDisabledBeforeDate = (
  itemUnderAnalysis: IItemUnderAnalysis | IndexOptionType | null,
): string | null => {
  if (!itemUnderAnalysis) {
    return null;
  }

  if (isPortfolioItemResponseDTO(itemUnderAnalysis)) {
    return itemUnderAnalysis.historyStartDate ?? null;
  }

  if (isStrategyItemResponseDTO(itemUnderAnalysis)) {
    return itemUnderAnalysis.historyStartDate ?? null;
  }

  if (isStrategyFactsheetResponseDTO(itemUnderAnalysis)) {
    return getDisabledBeforeDateToUse({
      effectiveHistoryStartDate: itemUnderAnalysis.strategy.effectiveHistoryStartDate,
      historyStartDate: itemUnderAnalysis.strategy.historyStartDate,
    });
  }

  if (isPortfolioTreeStrategy(itemUnderAnalysis)) {
    return getDisabledBeforeDateToUse({
      effectiveHistoryStartDate: itemUnderAnalysis.strategy?.effectiveHistoryStartDate,
      historyStartDate: itemUnderAnalysis.strategy?.historyStartDate,
    });
  }

  if (isPortfolioTreeSubportfolio(itemUnderAnalysis)) {
    return itemUnderAnalysis.historyStartDate ?? null;
  }

  return null;
};

export const getLiveDate = (itemUnderAnalysis: IItemUnderAnalysis | IndexOptionType | null): string | null => {
  if (!itemUnderAnalysis) {
    return null;
  }

  if (isStrategyFactsheetResponseDTO(itemUnderAnalysis)) {
    return itemUnderAnalysis.strategy.inceptionDate ?? null;
  }

  if (isStrategyItemResponseDTO(itemUnderAnalysis)) {
    return itemUnderAnalysis.inceptionDate ?? null;
  }

  if (isPortfolioTreeStrategy(itemUnderAnalysis)) {
    return itemUnderAnalysis.strategy?.inceptionDate ?? null;
  }

  if (isPortfolioTreeSubportfolio(itemUnderAnalysis)) {
    return itemUnderAnalysis.inceptionDate ?? null;
  }

  return null;
};
